import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Multi-file evolution nodes - The next evolution
const multiFileNodes = [
  // Title
  {
    id: 'title',
    data: { label: '🚀 Multi-File Evolution - The Next Generation' },
    position: { x: 450, y: 20 },
    style: { ...themeStyles.problemNode, width: 450 },
  },
  
  // Problem Space
  {
    id: 'problem-space',
    data: { label: '🎯 Complex Optimization Challenge\n"Optimize my microservice architecture"' },
    position: { x: 50, y: 120 },
    style: { ...themeStyles.uniqueFeatureNode, width: 280 },
  },
  
  // File Groups
  {
    id: 'group1',
    type: 'fileGroup',
    data: { 
      label: 'API Service Group',
      files: [
        { icon: '🌐', name: 'gateway.py' },
        { icon: '🔌', name: 'router.py' },
        { icon: '📡', name: 'handlers.py' }
      ]
    },
    position: { x: 50, y: 250 },
  },
  
  {
    id: 'group2',
    type: 'fileGroup',
    data: { 
      label: 'Data Processing Group',
      files: [
        { icon: '📊', name: 'processor.py' },
        { icon: '🔄', name: 'transformer.py' },
        { icon: '💾', name: 'storage.py' }
      ]
    },
    position: { x: 50, y: 420 },
  },
  
  {
    id: 'group3',
    type: 'fileGroup',
    data: { 
      label: 'Auth & Security Group',
      files: [
        { icon: '🔐', name: 'auth.py' },
        { icon: '🛡️', name: 'security.py' },
        { icon: '🔑', name: 'tokens.py' }
      ]
    },
    position: { x: 50, y: 590 },
  },
  
  // Interdependency Analysis
  {
    id: 'dependency-analysis',
    data: { label: '🔗 Interdependency Analysis\n• Cross-service communication\n• Data flow mapping\n• Performance bottlenecks' },
    position: { x: 350, y: 350 },
    style: { ...themeStyles.analysisNode, width: 240 },
  },
  
  // Evolution Engine V2
  {
    id: 'evolution-v2',
    data: { label: '🧬 Evolution Engine V2.0\nMulti-Dimensional Optimization' },
    position: { x: 650, y: 350 },
    style: { ...themeStyles.uniqueFeatureNode, width: 280 },
  },
  
  // Generation 1 - Group Combinations
  {
    id: 'gen1-label',
    data: { label: '🌱 Generation 1: Initial Group Optimizations' },
    position: { x: 350, y: 150 },
    style: { ...themeStyles.metricNode, width: 250 },
  },
  
  {
    id: 'combo1',
    type: 'fileGroup',
    data: { 
      label: 'Combination Alpha',
      files: [
        { icon: '🌐', name: 'Async gateway' },
        { icon: '📊', name: 'Stream processing' },
        { icon: '🔐', name: 'JWT caching' }
      ],
      combinedScore: '4.1/5'
    },
    position: { x: 350, y: 220 },
  },
  
  {
    id: 'combo2',
    type: 'fileGroup',
    data: { 
      label: 'Combination Beta',
      files: [
        { icon: '🌐', name: 'Load balancing' },
        { icon: '📊', name: 'Batch operations' },
        { icon: '🔐', name: 'Token pooling' }
      ],
      combinedScore: '4.3/5'
    },
    position: { x: 580, y: 220 },
  },
  
  {
    id: 'combo3',
    type: 'fileGroup',
    data: { 
      label: 'Combination Gamma',
      files: [
        { icon: '🌐', name: 'Circuit breaker' },
        { icon: '📊', name: 'Parallel processing' },
        { icon: '🔐', name: 'Session optimization' }
      ],
      combinedScore: '3.9/5'
    },
    position: { x: 810, y: 220 },
  },
  
  // Cross-pollination
  {
    id: 'cross-pollination',
    type: 'evolution',
    data: { 
      generation: 'Cross-Pollination',
      description: 'Best features from each group merge',
      scores: {
        'Performance': '4.5/5',
        'Scalability': '4.6/5',
        'Maintainability': '4.3/5'
      }
    },
    position: { x: 580, y: 480 },
  },
  
  // Generation 2 - Evolved Groups
  {
    id: 'gen2-label',
    data: { label: '🧬 Generation 2: Cross-Pollinated Solutions' },
    position: { x: 950, y: 350 },
    style: { ...themeStyles.metricNode, width: 250 },
  },
  
  {
    id: 'evolved-combo1',
    type: 'fileGroup',
    data: { 
      label: 'Evolved Solution A',
      files: [
        { icon: '⚡', name: 'Hybrid gateway' },
        { icon: '🚀', name: 'Smart streaming' },
        { icon: '🔒', name: 'Adaptive auth' }
      ],
      combinedScore: '4.7/5'
    },
    position: { x: 950, y: 420 },
  },
  
  {
    id: 'evolved-combo2',
    type: 'fileGroup',
    data: { 
      label: 'Evolved Solution B',
      files: [
        { icon: '⚡', name: 'Reactive routing' },
        { icon: '🚀', name: 'ML-optimized processing' },
        { icon: '🔒', name: 'Zero-trust security' }
      ],
      combinedScore: '4.9/5'
    },
    position: { x: 950, y: 590 },
  },
  
  // System-wide Impact
  {
    id: 'system-impact',
    data: { label: '📈 System-Wide Impact\n\n• Latency: -65%\n• Throughput: +180%\n• Resource Usage: -40%\n• Reliability: 99.99%' },
    position: { x: 650, y: 650 },
    style: { ...themeStyles.scoreNode, width: 200, height: 140, borderRadius: '20px', fontSize: '14px' },
  },
  
  // Final Solution
  {
    id: 'final-solution',
    data: { label: '🏆 Optimized Architecture\n\n✅ 12 files optimized\n🔗 Dependencies resolved\n📊 All metrics improved\n🚀 Production ready' },
    position: { x: 350, y: 750 },
    style: { ...themeStyles.uniqueFeatureNode, width: 280 },
  }
];

const multiFileEdges = [
  // Initial connections
  { id: 'e1', source: 'problem-space', target: 'group1', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'problem-space', target: 'group2', animated: true, style: themeStyles.edgeStyle },
  { id: 'e3', source: 'problem-space', target: 'group3', animated: true, style: themeStyles.edgeStyle },
  
  // To dependency analysis
  { id: 'e4', source: 'group1', target: 'dependency-analysis', style: themeStyles.feedbackEdgeStyle },
  { id: 'e5', source: 'group2', target: 'dependency-analysis', style: themeStyles.feedbackEdgeStyle },
  { id: 'e6', source: 'group3', target: 'dependency-analysis', style: themeStyles.feedbackEdgeStyle },
  
  // To evolution engine
  { id: 'e7', source: 'dependency-analysis', target: 'evolution-v2', animated: true, style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e8', source: 'evolution-v2', target: 'combo1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e9', source: 'evolution-v2', target: 'combo2', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'evolution-v2', target: 'combo3', style: themeStyles.evolutionEdgeStyle },
  
  // Cross-pollination
  { id: 'e11', source: 'combo1', target: 'cross-pollination', style: themeStyles.evolutionEdgeStyle },
  { id: 'e12', source: 'combo2', target: 'cross-pollination', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e13', source: 'combo3', target: 'cross-pollination', style: themeStyles.evolutionEdgeStyle },
  
  // Generation 2
  { id: 'e14', source: 'cross-pollination', target: 'evolved-combo1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e15', source: 'cross-pollination', target: 'evolved-combo2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Impact analysis
  { id: 'e16', source: 'evolved-combo1', target: 'system-impact', style: themeStyles.feedbackEdgeStyle },
  { id: 'e17', source: 'evolved-combo2', target: 'system-impact', animated: true, style: themeStyles.feedbackEdgeStyle },
  
  // Final solution
  { id: 'e18', source: 'system-impact', target: 'final-solution', animated: true, style: themeStyles.evolutionEdgeStyle },
];

const ArtemisMultiFileEvolution = () => {
  const [nodes, setNodes] = useState(multiFileNodes);
  const [edges] = useState(multiFileEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #141e30 0%, #243b55 50%, #428e92 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#ffffff', 
          padding: '20px', 
          margin: '0',
          fontFamily: 'Arial, sans-serif',
          fontSize: '28px',
          textShadow: '0 2px 10px rgba(0,0,0,0.5)'
        }}>
          🚀 Multi-File Evolution - Next Generation AI
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '20px',
          color: '#ffffff',
          fontSize: '14px',
          zIndex: 100
        }}>
          Evolving entire system architectures through intelligent group optimization and cross-pollination
        </div>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.5,
              maxZoom: 1.0
            }}
            minZoom={0.4}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(0,0,0,0.3)',
              }}
              nodeColor={(node) => {
                return node.style?.background || '#666';
              }}
              nodeStrokeColor={() => '#ffffff'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="rgba(255,255,255,0.05)" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Key Innovation */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'rgba(0,0,0,0.6)',
          padding: '15px',
          borderRadius: '15px',
          backdropFilter: 'blur(10px)',
          color: 'white',
          fontSize: '13px',
          maxWidth: '320px'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#4facfe' }}>
            🧬 Why Multi-File Evolution?
          </div>
          <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
            Traditional optimizers work on files in isolation. 
            Artemis V2 understands that <span style={{ color: '#f9d423' }}>real performance gains</span> come from 
            optimizing <span style={{ color: '#38ef7d' }}>interdependent systems together</span>.
          </div>
          <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(248, 54, 0, 0.2)', borderRadius: '8px' }}>
            <strong>Result:</strong> Up to <span style={{ color: '#ff6b35' }}>3x better performance</span> than 
            file-by-file optimization
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisMultiFileEvolution; 